import { useState, useRef, useEffect } from 'react'
import { formatSchoolYearLabel, getCurrentSchoolYear } from '@/utils/schoolYearHelpers'

interface SchoolYearSelectorProps {
  currentSchoolYear: string
  availableYears?: string[]
  onYearChange: (year: string) => void
  className?: string
}

export function SchoolYearSelector({
  currentSchoolYear,
  availableYears = ['2026-2027', '2025-2026'],
  onYearChange,
  className = ''
}: SchoolYearSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const activeSchoolYear = getCurrentSchoolYear()

  return (
    <div className={`relative inline-block ${className}`} dir="rtl" ref={dropdownRef}>
      {/* Trigger Select Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2.5 px-3.5 py-2 bg-white/90 backdrop-blur-md border border-gray-300/80 rounded-2xl shadow-sm hover:border-blue-500 hover:shadow-md transition-all duration-200 min-h-[44px] touch-manipulation group"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex items-center gap-1.5 text-right">
            <span className="text-xs font-semibold text-gray-500 font-hebrew hidden sm:inline">
              שנת לימודים:
            </span>
            <span className="text-xs font-bold text-gray-900 font-hebrew">
              {formatSchoolYearLabel(currentSchoolYear)}
            </span>
          </div>
        </div>

        {/* Chevron Icon */}
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-600' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Popover / Mobile Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-lg border border-gray-200 rounded-2xl shadow-xl z-50 p-1.5 animate-fadeIn space-y-1">
          <div className="px-3 py-1.5 text-[11px] font-bold text-gray-400 font-hebrew uppercase border-b border-gray-100">
            בחר שנת לימודים
          </div>

          {availableYears.map(year => {
            const isSelected = year === currentSchoolYear
            const isCurrent = year === activeSchoolYear

            return (
              <button
                key={year}
                type="button"
                onClick={() => {
                  onYearChange(year)
                  setIsOpen(false)
                }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold font-hebrew transition-all min-h-[44px] touch-manipulation ${
                  isSelected
                    ? 'bg-blue-50 text-blue-700 font-extrabold'
                    : 'text-gray-700 hover:bg-gray-100/70 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{formatSchoolYearLabel(year)}</span>
                  {isCurrent && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-emerald-100 text-emerald-800 rounded-full font-bold">
                      נוכחית
                    </span>
                  )}
                </div>

                {isSelected && (
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
